<script>
import { mapActions, mapGetters } from 'vuex';
import { Swiper, SwiperSlide } from 'swiper/vue';
import SwiperCore, { Navigation } from 'swiper';
import VSelect from '@/components/Common/VSelect';
import VInput from '@/components/Common/VInput';
import SvgPlus from '@/components/Svg/Plus';
import SvgArrow from '@/components/Svg/Arrow';
import SvgSearch from '@/components/Svg/Search';

SwiperCore.use([Navigation]);

const SWIPER_OPTIONS = {
  slidesPerView: 1.2,
  spaceBetween: 10,
  navigation: {
    nextEl: '.swiper-news-button-next',
    prevEl: '.swiper-news-button-prev',
  },
  breakpoints: {
    768: {
      slidesPerView: 2.2,
      spaceBetween: 20,
    },
    1024: {
      slidesPerView: 'auto',
      spaceBetween: 20,
    },
  },
};

export default {
  components: {
    Swiper,
    SwiperSlide,
    VSelect,
    VInput,
    SvgPlus,
    SvgArrow,
    SvgSearch,
  },
  props: {
    news: {
      type: Array,
      default: () => [],
    },
    brands: {
      type: Array,
      default: () => ({}),
    },
  },
  data() {
    return {
      brand: null,
      search: null,
      brands_array: [],
      news_data: this.news,
      timer: null,
    };
  },
  SWIPER_OPTIONS,
  computed: {
    ...mapGetters({
      newsGetData: 'News/news',
    }),
  },
  watch: {
    search() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.loadNews(this.urlParams());
      }, 500);
    },
    brand() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.loadNews(this.urlParams());
      }, 500);
    },
    newsGetData(newValue) {
      if (newValue) {
        this.news_data = newValue;
      }
    },
  },
  methods: {
    ...mapActions({
      loadNews: 'News/loadNews',
    }),

    allNews() {
      window.location.href = window.location.href + 'news/';
    },

    urlParams() {
      let params = '';
      this.brand?.forEach(brand => {
        params += `&brand[]=${brand.id}`;
      });
      if (this.search) {
        params += `&search=${this.search}`;
      }
      params = params.slice(1, params.length);
      return `${encodeURI(params)}`;
    },
  },
  created() {
    for (let key in this.brands) {
      this.brands_array.push(this.brands[key]);
    }
  },
};
</script>

<template>
  <div>
    <div class="flex flex-col md:flex-row justify-center space-y-4 md:space-x-4 md:space-y-0 items-center mt-5 md:mt-6 lg:mt-7 md:h-9">
      <v-select
        v-model="brand"
        :options="brands_array"
        theme="gray"
        label="name"
        multiple
        class="w-56 bg-gray-200 border-none placeholder-gray-800 py-2.5"
        placeholder="Марка"
      />
      <div class="flex relative bg-gray-200 rounded-brand h-9">
        <v-input
          v-model="search"
          class="w-56 border-none bg-gray-200 rounded-brand"
          classes-input="py-1.5 pr-7 border-none"
          placeholder="Поиск"
          :lazy="false"
        />
        <svg-search class="absolute top-1/2 -translate-y-1/2 right-3 text-gray-600" />
      </div>
      <div
        class="group w-fit text-sm font-medium py-1.5 px-4 bg-white rounded-md flex space-x-2.5 items-center hover:bg-blue-light h-9 cursor-pointer transition ease-in-out duration-200"
        @click="allNews()"
      >
        <span class="text-green group-hover:text-gray-800 h-auto text-sm transition ease-in-out duration-200">Все новости</span>
        <svg-plus class="group-hover:stroke-gray-dark stroke-green transition ease-in-out duration-200"></svg-plus>
      </div>
    </div>

    <swiper
      v-show="news_data.length"
      :slides-per-view="$options.SWIPER_OPTIONS.slidesPerView"
      :space-between="$options.SWIPER_OPTIONS.spaceBetween"
      :navigation="$options.SWIPER_OPTIONS.navigation"
      :breakpoints="$options.SWIPER_OPTIONS.breakpoints"
      class="swiper mt-10 !overflow-visible"
    >
      <swiper-slide
        v-for="item in news_data"
        :key="item.name"
        class="md:!w-[490px]"
      >
        <a
          :href="item.url"
          class="flex flex-col bg-white rounded-big-brand p-4 h-full transition duration-200 ease-in-out hover:shadow-brand"
        >
          <div class="h-28 lg:h-48 w-full rounded-lg overflow-hidden">
            <img
              :src="item.image"
              class="w-full h-full object-cover"
              alt=""
            />
          </div>
          <div class="mt-3.5 lg:mt-5 text-gray-800 font-medium text-xl mb-2.5">
            {{ item.name }}
          </div>
          <div class="flex justify-between mt-auto">
            <div class="text-gray-600 text-sm mt-auto">
              {{ item.publish_date }}
            </div>
            <div class="text-green hover:text-gray-800 lg:ml-auto mt-auto font-semibold text-base cursor-pointer transition ease-in-out duration-200">
              Подробнее
            </div>
          </div>
        </a>
      </swiper-slide>
    </swiper>
    <div
      v-show="news_data.length"
      class="relative flex space-x-3.5 items-center justify-center mt-5 md:mt-6 lg:mt-7"
    >
      <div class="swiper-news-button-prev group cursor-pointer flex justify-center items-center rounded-full border-2 border-gray-500 hover:bg-green hover:border-green w-12 lg:w-14 h-12 lg:h-14 transition ease-in-out duration-200 select-none">
        <svg-arrow class="w-2 h-2.5 group-hover:stroke-white stroke-gray-defaultGray rotate-180 transition ease-in-out duration-200"></svg-arrow>
      </div>
      <div class="swiper-news-button-next group cursor-pointer flex justify-center items-center rounded-full border-2 border-gray-500 hover:bg-green hover:border-green w-8 lg:w-10 h-8 lg:h-10 transition ease-in-out duration-200 select-none">
        <svg-arrow class="w-2 h-2.5 group-hover:stroke-white stroke-gray-defaultGray transition ease-in-out duration-200"></svg-arrow>
      </div>
    </div>
    <div
      v-show="!news_data.length"
      class="mt-5 text-center"
    >
      По вашему запросу ничего не найдено
    </div>
  </div>
</template>

<style scoped>
.swiper-container {
  overflow: unset;
}
</style>
